import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ChatRealtimeStoreService } from './services/others/chat-realtime-store.service';
import { InternalChatStoreService } from './services/others/internal-chat-store.service';
import { PresenceStatusTransition, UserPresenceActivityService } from './services/others/user-presence-activity.service';
import { SseService } from './services/api/sse.service';
import { ConversationDerivacionPopupSseEvent } from './models/sse-chatlink-events.model';
import { AvisoDerivacionEspecificaComponent } from './Dialog/aviso-derivacion-especifica/aviso-derivacion-especifica.component';
import { FrontendUpdateService } from './services/others/frontend-update.service';
import { AuthSessionService } from './services/others/auth-session.service';
import { ApiService } from './services/api/api.service';
import { NotificationService } from './services/others/toast-service.service';
import { WhatsappThemeService } from './services/others/whatsapp-theme.service';
import { ConversationDerivacionPopupData, DerivacionResponse } from './models/derivacion.model';

interface QueuedDerivacionPopup {
  identityKey: string;
  data: ConversationDerivacionPopupData;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatDialogModule],
  providers: [],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  isSesionIniciada = false;
  private popupSubscription: Subscription | null = null;
  private presenceTransitionSubscription: Subscription | null = null;
  private loginCompletedSubscription: Subscription | null = null;
  private chatStore: ChatRealtimeStoreService | null = null;
  private internalChatStore: InternalChatStoreService | null = null;
  private popupEnvelopeDedupCache = new Map<string, number>();
  private popupBusinessDedupCache = new Map<string, number>();
  private readonly popupEnvelopeDedupWindowMs = 120000;
  private readonly popupBusinessDedupWindowMs = 15000;
  private readonly derivationQueue: QueuedDerivacionPopup[] = [];
  private readonly queuedDerivationKeys = new Set<string>();
  private readonly shownDerivationKeys = new Set<string>();
  private readonly awaySeenDerivationKeys = new Set<string>();
  private derivationDialogOpen = false;
  private presenceReconciliationSequence = 0;
  private derivationReconciliationInProgress = false;
  private loginReconciliationPending = false;

  constructor(
    private injector: Injector,
    private sseService: SseService,
    private dialog: MatDialog,
    private userPresenceActivityService: UserPresenceActivityService,
    private frontendUpdateService: FrontendUpdateService,
    private authSessionService: AuthSessionService,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private whatsappThemeService: WhatsappThemeService,
  ) {}

  ngOnInit() {
    void this.whatsappThemeService.isDarkMode();
    const sessionUsername = sessionStorage.getItem('username')?.trim();
    if (sessionUsername) this.userPresenceActivityService.setOwnUsername(sessionUsername);
    this.frontendUpdateService.start();
    this.chatStore = this.injector.get(ChatRealtimeStoreService);
    this.internalChatStore = this.injector.get(InternalChatStoreService);
    this.presenceTransitionSubscription = this.userPresenceActivityService.presenceTransition$.subscribe(
      (transition) => this.onConfirmedPresenceTransition(transition),
    );
    this.loginCompletedSubscription = this.authSessionService.loginCompleted$.subscribe(() => {
      this.reconcileAfterLogin();
    });

    this.popupSubscription =
      this.sseService.conversationDerivacionPopupReceived.subscribe(
        (event: ConversationDerivacionPopupSseEvent) => {
          if (!this.authSessionService.isAuthenticated()) {
            return;
          }

          if (this.shouldSkipPopup(event)) {
            return;
          }

          if (this.chatStore?.consumeSelfAssignmentPopupSuppression?.(event)) {
            return;
          }
          const data = { ...event } as ConversationDerivacionPopupData;
          const identityKey = this.buildDerivationIdentityKey(data);
          if (this.userPresenceActivityService.confirmedEstadoConexion() === 'AUSENTE') {
            this.markDerivationSeenWhileAway(identityKey);
          }
          this.enqueueDerivation(data, identityKey);
        },
      );

    const authenticated = this.authSessionService.isAuthenticated();
    if (authenticated && this.chatStore) {
      this.chatStore.init();
    }
    if (authenticated && this.internalChatStore) {
      this.internalChatStore.init();
    }
    if (authenticated) {
      this.reconcileAfterLogin();
      this.authSessionService.start(
        sessionStorage.getItem('tokenExpiresAt'),
        sessionStorage.getItem('tokenRefreshAt'),
      );
      void this.authSessionService.ensureCsrfToken().then((csrfReady) => {
        if (csrfReady && this.authSessionService.isAuthenticated()) {
          this.userPresenceActivityService.start();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.frontendUpdateService.stop();
    this.userPresenceActivityService.stop();
    this.authSessionService.stop();
    this.popupSubscription?.unsubscribe();
    this.popupSubscription = null;
    this.presenceTransitionSubscription?.unsubscribe();
    this.presenceTransitionSubscription = null;
    this.loginCompletedSubscription?.unsubscribe();
    this.loginCompletedSubscription = null;
    this.presenceReconciliationSequence++;
    this.derivationQueue.length = 0;
    this.queuedDerivationKeys.clear();
    this.shownDerivationKeys.clear();
    this.awaySeenDerivationKeys.clear();
    this.popupEnvelopeDedupCache.clear();
    this.popupBusinessDedupCache.clear();
  }

  private onConfirmedPresenceTransition(transition: PresenceStatusTransition): void {
    if (transition.status === 'AUSENTE' && transition.previousStatus !== 'AUSENTE') {
      this.awaySeenDerivationKeys.clear();
      this.clearAwaySeenStorage(transition.username);
      return;
    }

    if (
      transition.status === 'CONECTADO' &&
      (transition.previousStatus === 'AUSENTE' || transition.previousStatus === 'DESCONECTADO')
    ) {
      if (this.loginReconciliationPending) return;
      if (transition.previousStatus === 'AUSENTE') {
        this.shownDerivationKeys.clear();
      }
      this.startDerivationReconciliation(transition.username, true, false);
    }
  }

  private reconcileAfterLogin(): void {
    const username = (sessionStorage.getItem('username') || this.userPresenceActivityService.username() || '').trim();
    this.shownDerivationKeys.clear();
    this.awaySeenDerivationKeys.clear();
    this.clearAwaySeenStorage(username);
    this.loginReconciliationPending = true;
    this.startDerivationReconciliation(username, false, true);
  }

  private startDerivationReconciliation(username: string, requireConnected: boolean, isLogin: boolean): void {
    const sequence = ++this.presenceReconciliationSequence;
    this.derivationReconciliationInProgress = true;
    void this.reconcileOpenDerivations(username, sequence, requireConnected, isLogin);
  }

  private async reconcileOpenDerivations(
    username: string,
    sequence: number,
    requireConnected: boolean,
    isLogin: boolean,
  ): Promise<void> {
    const awaySeenKeys = this.loadAwaySeenKeys(username);
    const assignedDerivations: DerivacionResponse[] = [];
    let querySucceeded = false;
    try {
      if (!this.authSessionService.isAuthenticated()) return;
      const pageSize = 100;
      let pageNumber = 0;
      let hasNext = true;
      while (hasNext) {
        const page = await firstValueFrom(
          this.apiService.getDerivacionesAbiertasAsignadas(pageNumber, pageSize),
        );
        assignedDerivations.push(...(page.content || []));
        hasNext = !!page.hasNext;
        pageNumber++;
        if (pageNumber > 1000) throw new Error('La consulta devolvió demasiadas páginas');
      }
      querySucceeded = true;
    } catch (error) {
      if (sequence === this.presenceReconciliationSequence && this.authSessionService.isAuthenticated()) {
        this.notificationService.notify(
          'No se pudieron consultar las derivaciones abiertas. Se intentará nuevamente al iniciar sesión o al regresar a Conectado.',
          'info',
          6000,
        );
      }
      console.error('No se pudieron consultar las derivaciones abiertas asignadas:', error);
    } finally {
      if (sequence === this.presenceReconciliationSequence) {
        if (isLogin) this.loginReconciliationPending = false;
        const sessionUsername = (sessionStorage.getItem('username') || '').trim();
        const isSameSessionUser = !username || !sessionUsername || sessionUsername.toLowerCase() === username.toLowerCase();
        const canPresentResults = this.authSessionService.isAuthenticated() && isSameSessionUser &&
          (!requireConnected || (
            this.userPresenceActivityService.username() === username &&
            this.userPresenceActivityService.confirmedEstadoConexion() === 'CONECTADO'
          ));

        if (canPresentResults && querySucceeded) {
          for (const derivation of assignedDerivations) {
            const identityKey = this.buildDerivationIdentityKey(derivation);
            if (!awaySeenKeys.has(identityKey)) this.enqueueDerivation(derivation, identityKey, false);
          }
          this.awaySeenDerivationKeys.clear();
          this.clearAwaySeenStorage(username);
        }

        this.derivationReconciliationInProgress = false;
        this.openNextDerivationDialog();
      }
    }
  }

  private enqueueDerivation(data: ConversationDerivacionPopupData, identityKey: string, openDialog = true): void {
    if (this.queuedDerivationKeys.has(identityKey) || this.shownDerivationKeys.has(identityKey)) return;
    this.queuedDerivationKeys.add(identityKey);
    this.shownDerivationKeys.add(identityKey);
    this.derivationQueue.push({ identityKey, data });
    if (openDialog) this.openNextDerivationDialog();
  }

  private openNextDerivationDialog(): void {
    const deferForReconciliation = this.derivationReconciliationInProgress &&
      this.userPresenceActivityService.confirmedEstadoConexion() !== 'AUSENTE';
    if (this.derivationDialogOpen || deferForReconciliation || this.derivationQueue.length === 0) return;
    if (!this.authSessionService.isAuthenticated()) {
      this.derivationQueue.length = 0;
      this.queuedDerivationKeys.clear();
      return;
    }

    const batch = this.derivationQueue.splice(0, this.derivationQueue.length);
    this.derivationDialogOpen = true;
    this.dialog.open(AvisoDerivacionEspecificaComponent, {
      width: '800px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: 'dialog',
      restoreFocus: true,
      disableClose: true,
      data: batch.map((entry) => entry.data),
    }).afterClosed().subscribe(() => {
      this.derivationDialogOpen = false;
      for (const entry of batch) this.queuedDerivationKeys.delete(entry.identityKey);
      this.openNextDerivationDialog();
    });
  }

  private buildDerivationIdentityKey(data: ConversationDerivacionPopupData | DerivacionResponse): string {
    return [
      String(data.conversacionId ?? ''),
      String(data.waId ?? '').trim(),
      String(data.areaId ?? ''),
      String(data.usuarioAsignado ?? '').trim().toLowerCase(),
      String(data.fechaDerivacion ?? '').trim(),
    ].join('|');
  }

  private markDerivationSeenWhileAway(identityKey: string): void {
    this.awaySeenDerivationKeys.add(identityKey);
    const username = this.userPresenceActivityService.username();
    if (!username) return;
    const storedKeys = this.loadAwaySeenKeys(username);
    storedKeys.add(identityKey);
    try {
      sessionStorage.setItem(this.awaySeenStorageKey(username), JSON.stringify([...storedKeys]));
    } catch {
      // The in-memory set still prevents duplicate notices for this page session.
    }
  }

  private loadAwaySeenKeys(username: string): Set<string> {
    const keys = new Set(this.awaySeenDerivationKeys);
    if (!username) return keys;
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.awaySeenStorageKey(username)) || '[]');
      if (Array.isArray(saved)) saved.forEach((key) => typeof key === 'string' && keys.add(key));
    } catch {
      this.clearAwaySeenStorage(username);
    }
    return keys;
  }

  private clearAwaySeenStorage(username: string): void {
    if (!username) return;
    try {
      sessionStorage.removeItem(this.awaySeenStorageKey(username));
    } catch {
      // Storage can be unavailable in restricted browsing modes.
    }
  }

  private awaySeenStorageKey(username: string): string {
    return `sgw.derivaciones.vistas-durante-ausencia:${encodeURIComponent(username.toLowerCase())}`;
  }

  private shouldSkipPopup(event: ConversationDerivacionPopupSseEvent): boolean {
    const now = Date.now();
    this.cleanupPopupDedupCache(now);

    const envelopeKey = String(event.sseEnvelopeId || '').trim();
    if (envelopeKey) {
      const envelopeSeenAt = this.popupEnvelopeDedupCache.get(envelopeKey);
      if (envelopeSeenAt && now - envelopeSeenAt < this.popupEnvelopeDedupWindowMs) {
        return true;
      }
      this.popupEnvelopeDedupCache.set(envelopeKey, now);
    }

    const dedupKey = this.buildPopupDedupKey(event);
    const lastSeenAt = this.popupBusinessDedupCache.get(dedupKey);
    if (lastSeenAt && now - lastSeenAt < this.popupBusinessDedupWindowMs) {
      return true;
    }

    this.popupBusinessDedupCache.set(dedupKey, now);
    return false;
  }

  private buildPopupDedupKey(event: ConversationDerivacionPopupSseEvent): string {
    const conversacionId = String(event.conversacionId ?? 0);
    const waId = String(event.waId ?? '').trim();
    const eventType = String(event.sseEventType || 'CONVERSATION_DERIVACION_POPUP').trim();
    const areaId = String(event.areaId ?? 0);
    const rolId = String(event.rolId ?? 0);
    const usuarioAsignado = String(event.usuarioAsignado || '').trim().toLowerCase();
    const nombreCompletoAsignado = String(event.nombreCompletoAsignado || '').trim().toLowerCase();
    const areaDescripcion = String(event.areaDescripcion || '').trim().toLowerCase();
    const fechaDerivacion = String(event.fechaDerivacion || '').trim();
    const observacion = String(event.observacion || '').trim().toLowerCase();
    const mensaje = String(event.mensaje || '').trim().toLowerCase();
    const specificUserFlag = event.notificacionUsuarioEspecifico ? '1' : '0';
    const assignedFlag = event.asignada ? '1' : '0';
    const automaticFlag = event.automatica ? '1' : '0';

    return [
      conversacionId,
      waId,
      eventType,
      areaId,
      rolId,
      usuarioAsignado,
      nombreCompletoAsignado,
      areaDescripcion,
      fechaDerivacion,
      observacion,
      mensaje,
      specificUserFlag,
      assignedFlag,
      automaticFlag,
    ].join('|');
  }

  private cleanupPopupDedupCache(now: number): void {
    for (const [key, timestamp] of this.popupEnvelopeDedupCache.entries()) {
      if (now - timestamp >= this.popupEnvelopeDedupWindowMs) {
        this.popupEnvelopeDedupCache.delete(key);
      }
    }

    for (const [key, timestamp] of this.popupBusinessDedupCache.entries()) {
      if (now - timestamp >= this.popupBusinessDedupWindowMs) {
        this.popupBusinessDedupCache.delete(key);
      }
    }
  }

}
